import Role from "@/models/Role";
import { setDisplayedItems } from "@/utils/setDisplayedItems";
import { ROLE_STATUS } from "@/models/enums/roleStatuses";
import { list } from "@/utils/factoryRest";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { RolesApi } from "@socotec.io/certipers-back-sdk";
import configs from "@/setup/openApiConfig";

const RoleRepo = useRepo(Role);
const rolesApi = new RolesApi(configs.certipersBackConfig);

export const formatRole = (role) => {
  return {
    uuid: role.uuid,
    domainUuid: role.domain,
    userUuid: role.user,
    roleType: role.roleType,
    status: role.status,
    certipersDocuments: role.certipersDocuments,
  };
};

export const useRoleStore = defineStore("role", {
  state: () => ({
    itemsCount: 0,
  }),

  actions: {
    fetchRoles: list({
      ModelRepo: RoleRepo,
      restListMethod: rolesApi.list.bind(rolesApi),
      handleResponseData: ({ results }) => {
        return results.map(formatRole);
      },
      onItemsInserted: (response) => {
        useRoleStore().setItemsCount(response.count);
        return setDisplayedItems(RoleRepo, response.results);
      },
    }),

    async createRole({ role }) {
      const response = await rolesApi.create({
        roleRequest: role,
      });
      RoleRepo.save(formatRole(response));
    },

    async bulkCreateRoles({ roles }) {
      const response = await rolesApi.bulkCreate({
        roleBulkCreateRequestRequest: { roles },
      });
      RoleRepo.save(response.map(formatRole));
    },

    async partialUpdateRole({ role, fields }) {
      const response = await rolesApi.partialUpdate({
        uuid: role.uuid,
        patchedRoleRequest: Object.fromEntries(fields.map((field) => [field, role[field]])),
      });
      return RoleRepo.save(formatRole(response));
    },

    async destroyRole(roleUuid) {
      await rolesApi.destroy({ uuid: roleUuid.uuid });
      return RoleRepo.destroy(roleUuid.uuid);
    },

    setItemsCount(count) {
      this.itemsCount = count;
    },
  },

  getters: {
    getUserRoles: () => (userUuid) => {
      return RoleRepo.where("userUuid", userUuid).with("domainData").get();
    },
    getPendingRoles: () => {
      return RoleRepo.where("isDisplayed", true)
        .where("status", ROLE_STATUS.PENDING)
        .with("domainData")
        .with("userData")
        .get();
    },
    getRolesCount: (state) => {
      return state.itemsCount;
    },
  },
});
